(() => {
  'use strict';
  if (window.KinriotReviewLightbox) return;

  let labels = {};
  try { labels = JSON.parse(document.querySelector('[data-kr-review-labels]')?.textContent || '{}'); } catch (_) { /* Use readable fallback labels. */ }
  const label = (key, fallback) => typeof labels[key] === 'string' ? labels[key] : fallback;

  const string = (value) => typeof value === 'string' ? value : '';
  const element = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  };
  const imageURL = (value) => {
    if (!string(value)) return '';
    try {
      const url = new URL(value, window.location.origin);
      const host = url.hostname.toLowerCase();
      const allowed = host === 'cdn.shopify.com' || host.endsWith('.shopifycdn.com') || host === 'storage.trustifyreview.com' ||
        (url.origin === window.location.origin && url.pathname.startsWith('/cdn/'));
      return url.protocol === 'https:' && !url.username && !url.password && allowed ? url.href : '';
    } catch (_) { return ''; }
  };
  const productURL = (handle) => {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(string(handle))) return '';
    const root = window.Shopify?.routes?.root;
    const prefix = typeof root === 'string' && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root) ? root : '/';
    return `${prefix}products/${handle}`;
  };
  const normalize = (item, sourceIndex) => {
    const src = imageURL(item?.src);
    if (!src) return null;
    const raw = item?.review && typeof item.review === 'object' ? item.review : {};
    const rating = Number(raw.rating);
    return {
      src, thumbnail: imageURL(item.thumbnail) || src,
      alt: string(item.alt) || label('photo_alt', 'Customer review photo'), sourceIndex,
      review: {
        id: raw.id, reviewer_name: string(raw.reviewer_name), title: string(raw.title), content: string(raw.content),
        rating: Number.isFinite(rating) && rating > 0 && rating <= 5 ? rating : 0,
        created_at: string(raw.created_at), is_verified: raw.is_verified === true,
        product: { title: string(raw.product?.title), url: productURL(raw.product?.handle) }
      }
    };
  };

  let dialog, ui, items = [], activeIndex = 0, returnFocus = null, locked = false;
  const button = (className, label, glyph) => {
    const node = element('button', className, glyph);
    node.type = 'button';
    node.setAttribute('aria-label', label);
    return node;
  };
  const finishClose = () => {
    if (locked) {
      document.documentElement.classList.remove('kr-review-lightbox-open');
      locked = false;
    }
    const target = returnFocus;
    returnFocus = null;
    if (target?.isConnected && typeof target.focus === 'function') target.focus({ preventScroll: true });
  };
  const close = () => {
    if (!dialog?.open) return;
    dialog.close();
    finishClose();
  };
  const setDetails = (show) => {
    dialog.dataset.details = String(show);
    ui.details.hidden = !show;
    ui.toggle.textContent = show ? label('expand', 'Expand photo') : label('show_review', 'Show review');
    ui.toggle.setAttribute('aria-label', show ? label('expand_label', 'Expand photo and hide review details') : label('show_review_label', 'Show review details'));
    ui.toggle.setAttribute('aria-expanded', String(show));
  };
  const showIndex = (index, scrollThumb = true) => {
    if (!items.length) return;
    activeIndex = (index + items.length) % items.length;
    const item = items[activeIndex];
    const review = item.review;
    ui.image.hidden = false;
    ui.imageError.hidden = true;
    ui.image.src = item.src;
    ui.image.alt = item.alt;
    ui.counter.textContent = `${activeIndex + 1} / ${items.length}`;
    ui.previous.hidden = ui.next.hidden = items.length < 2;
    ui.rating.hidden = !review.rating;
    ui.rating.setAttribute('aria-label', review.rating ? label('stars_label', '[rating] out of 5 stars').replace('[rating]', String(review.rating)) : label('rating_unavailable', 'Rating unavailable'));
    ui.rating.style.setProperty('--kr-review-rating', `${review.rating * 20}%`);
    ui.date.hidden = true;
    ui.date.textContent = '';
    ui.date.removeAttribute('datetime');
    const date = new Date(review.created_at);
    if (review.created_at && Number.isFinite(date.getTime())) {
      ui.date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
      ui.date.dateTime = date.toISOString();
      ui.date.hidden = false;
    }
    ui.name.textContent = review.reviewer_name;
    ui.name.hidden = !review.reviewer_name;
    ui.verified.hidden = !review.is_verified;
    ui.title.textContent = review.title;
    ui.title.hidden = !review.title;
    ui.content.textContent = review.content;
    ui.content.hidden = !review.content;
    ui.product.hidden = !review.product.title || !review.product.url;
    ui.productLink.textContent = review.product.title;
    if (review.product.url) ui.productLink.setAttribute('href', review.product.url);
    else ui.productLink.removeAttribute('href');
    ui.empty.hidden = Boolean(review.rating || review.reviewer_name || review.title || review.content || review.product.title);
    ui.details.scrollTop = 0;
    ui.thumbs.querySelectorAll('button').forEach((thumb, i) => {
      thumb.setAttribute('aria-current', String(i === activeIndex));
      if (scrollThumb && i === activeIndex) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };
  const build = () => {
    if (dialog) return typeof dialog.showModal === 'function';
    dialog = element('dialog', 'kr-review-lightbox');
    if (typeof dialog.showModal !== 'function') { dialog = null; return false; }
    dialog.setAttribute('aria-label', label('dialog_label', 'Customer review photos and details'));
    const shell = element('div', 'kr-review-lightbox__shell');
    const media = element('section', 'kr-review-lightbox__media');
    media.setAttribute('aria-label', label('photos_label', 'Customer review photos'));
    const toolbar = element('div', 'kr-review-lightbox__toolbar');
    const toggle = button('kr-review-lightbox__toggle', label('expand', 'Expand photo'), label('expand', 'Expand photo'));
    const closeButton = button('kr-review-lightbox__close', label('close', 'Close review photos'), '×');
    const figure = element('div', 'kr-review-lightbox__figure');
    const image = element('img', 'kr-review-lightbox__image');
    image.decoding = 'async';
    const imageError = element('p', 'kr-review-lightbox__image-error', label('photo_error', 'This photo could not be loaded. Try another photo.'));
    imageError.hidden = true;
    const previous = button('kr-review-lightbox__arrow kr-review-lightbox__arrow--previous', label('previous', 'Previous customer photo'), '‹');
    const next = button('kr-review-lightbox__arrow kr-review-lightbox__arrow--next', label('next', 'Next customer photo'), '›');
    const thumbs = element('div', 'kr-review-lightbox__thumbs');
    thumbs.setAttribute('aria-label', label('choose_photo', 'Choose a customer photo'));
    const counter = element('p', 'kr-review-lightbox__counter');
    counter.setAttribute('role', 'status');
    counter.setAttribute('aria-live', 'polite');
    const details = element('aside', 'kr-review-lightbox__details');
    details.id = 'KrReviewLightboxDetails';
    details.setAttribute('aria-label', label('details_label', 'Review details for the selected photo'));
    toggle.setAttribute('aria-controls', details.id);
    const meta = element('div', 'kr-review-lightbox__meta');
    const rating = element('span', 'kr-review-lightbox__rating');
    rating.setAttribute('role', 'img');
    ['base', 'fill'].forEach((part) => {
      const stars = element('span', `kr-review-lightbox__rating-${part}`, '★★★★★');
      stars.setAttribute('aria-hidden', 'true');
      rating.appendChild(stars);
    });
    const date = element('time', 'kr-review-lightbox__date');
    const byline = element('div', 'kr-review-lightbox__byline');
    const name = element('strong', 'kr-review-lightbox__name');
    const verified = element('span', 'kr-review-lightbox__verified', label('verified', 'Verified'));
    const title = element('h3', 'kr-review-lightbox__title');
    const content = element('p', 'kr-review-lightbox__content');
    const product = element('p', 'kr-review-lightbox__product', label('reviewed_product', 'Reviewed product'));
    const productLink = element('a', 'kr-review-lightbox__product-link');
    const empty = element('p', 'kr-review-lightbox__empty', label('details_unavailable', 'Review details are unavailable for this photo.'));
    toolbar.append(toggle, closeButton);
    figure.append(image, imageError, previous, next);
    media.append(figure, thumbs, counter);
    meta.append(rating, date);
    byline.append(name, verified);
    product.appendChild(productLink);
    details.append(meta, byline, title, content, product, empty);
    shell.append(toolbar, media, details);
    dialog.appendChild(shell);
    document.body.appendChild(dialog);
    ui = { image, imageError, previous, next, thumbs, counter, details, rating, date, name, verified, title, content, product, productLink, empty, toggle, closeButton };
    closeButton.addEventListener('click', close);
    toggle.addEventListener('click', () => setDetails(dialog.dataset.details !== 'true'));
    previous.addEventListener('click', () => showIndex(activeIndex - 1));
    next.addEventListener('click', () => showIndex(activeIndex + 1));
    image.addEventListener('error', () => { image.hidden = true; imageError.hidden = false; });
    image.addEventListener('load', () => { image.hidden = false; imageError.hidden = true; });
    thumbs.addEventListener('click', (event) => {
      const target = event.target.closest('button[data-index]');
      if (target && thumbs.contains(target)) showIndex(Number(target.dataset.index));
    });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    dialog.addEventListener('close', () => { if (!dialog.open) finishClose(); });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        showIndex(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
      if (event.key === 'Tab') {
        const focusable = [...dialog.querySelectorAll('button:not([disabled]),a[href]')].filter((node) => !node.hidden && !node.closest('[hidden]') && node.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    });
    return true;
  };

  window.KinriotReviewLightbox = Object.freeze({
    open(options = {}) {
      if (!Array.isArray(options.items)) return false;
      const incoming = options.items.map(normalize).filter(Boolean);
      if (!incoming.length || !build()) return false;
      const requested = Number.isInteger(options.index) ? options.index : 0;
      items = incoming;
      const desired = items.findIndex((item) => item.sourceIndex === requested);
      const wasOpen = dialog.open;
      returnFocus = options.trigger && typeof options.trigger.focus === 'function' ? options.trigger : document.activeElement;
      ui.thumbs.replaceChildren();
      items.forEach((item, index) => {
        const thumb = button('kr-review-lightbox__thumb', label('view_photo', 'View customer photo [number]').replace('[number]', String(index + 1)), '');
        thumb.dataset.index = String(index);
        const image = element('img');
        image.src = item.thumbnail;
        image.alt = '';
        image.loading = index < 8 ? 'eager' : 'lazy';
        image.decoding = 'async';
        thumb.appendChild(image);
        ui.thumbs.appendChild(thumb);
      });
      setDetails(options.details !== false);
      showIndex(desired < 0 ? 0 : desired, false);
      try {
        if (!wasOpen) dialog.showModal();
        document.documentElement.classList.add('kr-review-lightbox-open');
        locked = true;
        ui.closeButton.focus({ preventScroll: true });
        return true;
      } catch (_) { finishClose(); return false; }
    },
    close
  });
})();
